import clsx from "clsx";
import { FunctionComponent } from "react";
import { Alert, Button, ButtonToolbar, Form, Spinner } from "react-bootstrap";
import { RenderWhen } from "../../../../../components";
import { DropdownFilterDataProps } from "../../../../../components/DropdownFilter";
import Typography from "../../../../../components/typography/Typography";
import { form, FormLabel } from "../../../../../forms";
import { FormProps } from "../../../../../forms/@types/form";
import { WidgetPassword } from "../../../../../forms/components/WidgetPassword";
import { useSystemVersion } from "../../../../../hooks";
import {
  AnalysisTypes,
  areAdditionalAnalysisTypesAvailable
} from "../../../../header/hooks/usePrepareReanalyzeData";
import ReanalyzeCardContainer from "../../reanalyze/reanalyze-card-container/ReanalyzeCardContainer";
import ReanalyzeFormInput, {
  LAYOUT_TYPE
} from "../reanalyze-form-input/ReanalyzeFormInput";
import RlcsConfigurationInput from "../rlcs-configuration-input/RlcsConfigurationInput";
import styles from "./reanalyze-form.module.scss";

export const DEFAULT_MENU_STYLES = {
  maxHeight: 211,
  overflow: "auto",
  width: "100%"
};

export type RlcsParams = {
  platform?: string;
  geolocation?: string;
  locale?: string;
  interactive?: boolean;
};

type ReanalyzeFormProps = {
  onSubmit: () => void;
  onCancel: () => void;
  popupWindowData: AnalysisTypes;
  onRlcsParamsChange: (value: RlcsParams) => void;
  rlcsParams: RlcsParams;
  tcAnalysisDisabled: boolean;
  ticloudAnalysisDisabled: boolean;
  interactiveAnalysisDisabled: boolean;
  submitButtonLabel: string;
  onFilePasswordChange: (value: string) => void;
  showFilePasswordField: boolean;
  displayWarningMessage: boolean;
  isSubmitted: boolean;
} & FormProps;

const ReanalyzeForm: FunctionComponent<ReanalyzeFormProps> = ({
  onSubmit = () => undefined,
  onCancel = () => undefined,
  onValues,
  popupWindowData,
  values,
  displayWarningMessage,
  onRlcsParamsChange,
  rlcsParams,
  tcAnalysisDisabled = false,
  ticloudAnalysisDisabled = false,
  submitButtonLabel = "Reanalyze",
  onFilePasswordChange,
  showFilePasswordField = true,
  interactiveAnalysisDisabled = false,
  isSubmitted = false
}) => {
  const onChangeRlcsPlatform = (value: DropdownFilterDataProps<string>) => {
    onRlcsParamsChange({
      ...rlcsParams,
      platform: value.value
    });
  };

  const onChangeRlcsLocale = (value: DropdownFilterDataProps<string>) => {
    onRlcsParamsChange({
      ...rlcsParams,
      locale: value.value
    });
  };

  const onChangeRlcsGeolocation = (value: DropdownFilterDataProps<string>) => {
    onRlcsParamsChange({
      ...rlcsParams,
      geolocation: value.value
    });
  };

  const toggleRlcsInteractive = (updatedPlatform: string) => {
    onRlcsParamsChange({
      ...rlcsParams,
      interactive: !rlcsParams.interactive,
      platform: updatedPlatform
    });
  };

  const { tc, ti_cloud } = popupWindowData;
  const {
    rl_dynamic_analysis,
    rl_auxiliary_analysis,
    cape,
    cisco_secure_malware_analytics,
    cuckoo,
    fireeye,
    joe,
    vmray_tcbase
  } = popupWindowData.dynamic_analysis;
  const { ticoreVersion } = useSystemVersion();

  const isSubmitButtonDisabled = !Object.values(values).some(Boolean);

  const toggleValue = (fieldName: string) => {
    onValues?.({ ...values, [fieldName]: !values[fieldName] });
  };

  return (
    <Form onSubmit={onSubmit} className="reanalyze-form">
      <div>
        {displayWarningMessage && (
          <div>
            <Alert
              variant="warning"
              className="mb-25"
              style={{ marginTop: "-25px" }}
            >
              <p>
                <b>Warning:</b> you are submitting several samples for
                reanalysis. Depending on their size and number, the appliance
                performance and the sample processing speed may be impacted.
              </p>
            </Alert>
          </div>
        )}
        <div className={"d-flex gap-30"}>
          <div className={"flex-grow-1"}>
            <Typography variant={"h5"} className={"mb-15"}>
              RL Analysis
            </Typography>

            <ReanalyzeCardContainer
              disabled={!tc.enabled || tcAnalysisDisabled}
              selected={values.tc}
              layout={LAYOUT_TYPE.ROW}
              tooltipMessage={
                tcAnalysisDisabled
                  ? "Static Analysis is mandatory when submitting a file."
                  : undefined
              }
              onClick={() => {
                if (!tc.enabled || tcAnalysisDisabled) {
                  return;
                }
                toggleValue("tc");
              }}
            >
              <ReanalyzeFormInput
                name="tc"
                label="Static Analysis"
                labelIcon={"static-analysis"}
                labelIconSize={24}
                help={`Spectra Core v${ticoreVersion}`}
                helpImportant={
                  !tc.enabled && tc.additional_information
                    ? tc.additional_information
                    : null
                }
                timestamp={tc.last_completed_on}
                disabled={!tc.enabled || tcAnalysisDisabled}
                layout={LAYOUT_TYPE.ROW}
                checked={values.tc}
              />
            </ReanalyzeCardContainer>

            <ReanalyzeCardContainer
              disabled={!ti_cloud.enabled || ticloudAnalysisDisabled}
              selected={values.ti_cloud}
              layout={LAYOUT_TYPE.ROW}
              tooltipMessage={
                ticloudAnalysisDisabled
                  ? "File is automatically submitted to Threat Intelligence if RL Cloud Sandbox is selected."
                  : undefined
              }
              onClick={() => {
                if (!ti_cloud.enabled || ticloudAnalysisDisabled) {
                  return;
                }
                toggleValue("ti_cloud");
              }}
            >
              <ReanalyzeFormInput
                name="ti_cloud"
                label="Threat Intelligence"
                labelIcon="cloud-intelligence"
                labelIconSize={24}
                help={"Spectra Intelligence"}
                helpImportant={ti_cloud.additional_information}
                timestamp={ti_cloud.last_completed_on}
                disabled={!ti_cloud.enabled || ticloudAnalysisDisabled}
                layout={LAYOUT_TYPE.ROW}
                checked={values.ti_cloud}
              />
            </ReanalyzeCardContainer>

            <RlcsConfigurationInput
              rlcsSelected={values.rl_dynamic_analysis}
              analysisInfo={rl_dynamic_analysis}
              rlcsParams={rlcsParams}
              interactiveAnalysisDisabled={interactiveAnalysisDisabled}
              toggleRlcsSelected={() => toggleValue("rl_dynamic_analysis")}
              toggleRlcsInteractiveSelected={toggleRlcsInteractive}
              onChangeRlcsPlatform={onChangeRlcsPlatform}
              onChangeRlcsLocale={onChangeRlcsLocale}
              onChangeRlcsGeolocation={onChangeRlcsGeolocation}
            />

            <ReanalyzeCardContainer
              disabled={!rl_auxiliary_analysis.enabled}
              selected={values.rl_auxiliary_analysis}
              layout={LAYOUT_TYPE.ROW}
              onClick={() => {
                if (!rl_auxiliary_analysis.enabled) {
                  return;
                }
                toggleValue("rl_auxiliary_analysis");
              }}
              tooltipMessage={
                rl_auxiliary_analysis.enabled
                  ? undefined
                  : rl_auxiliary_analysis.additional_information
              }
            >
              <ReanalyzeFormInput
                name="rl_auxiliary_analysis"
                label="Auxiliary Analysis"
                labelIcon="auxiliary-analysis"
                labelIconSize={24}
                helpImportant={
                  rl_auxiliary_analysis.enabled
                    ? rl_auxiliary_analysis.additional_information
                    : undefined
                }
                timestamp={rl_auxiliary_analysis.last_completed_on}
                disabled={!rl_auxiliary_analysis.enabled}
                layout={LAYOUT_TYPE.ROW}
                checked={values.rl_auxiliary_analysis}
              />
            </ReanalyzeCardContainer>
          </div>

          <RenderWhen
            condition={Boolean(
              areAdditionalAnalysisTypesAvailable(popupWindowData)
            )}
          >
            <div
              className={clsx("ps-10", styles.integration_analysis_container)}
            >
              <Typography variant={"h5"} className={"mb-15"}>
                Integration Analysis
              </Typography>

              <ReanalyzeCardContainer
                hidden={!cape.enabled}
                selected={values.cape}
                layout={LAYOUT_TYPE.COL}
                onClick={() => toggleValue("cape")}
              >
                <ReanalyzeFormInput
                  name="cape"
                  label="CAPE Sandbox"
                  labelIcon="cape"
                  labelIconSize={16}
                  helpImportant={cape.additional_information}
                  timestamp={cape.last_completed_on}
                  layout={LAYOUT_TYPE.COL}
                  checked={values.cape}
                />
              </ReanalyzeCardContainer>

              <ReanalyzeCardContainer
                hidden={!cisco_secure_malware_analytics.enabled}
                selected={values.cisco_secure_malware_analytics}
                layout={LAYOUT_TYPE.COL}
                onClick={() => toggleValue("cisco_secure_malware_analytics")}
              >
                <ReanalyzeFormInput
                  name="cisco_secure_malware_analytics"
                  label="Cisco Secure Malware Analytics"
                  labelIcon="cisco_secure_malware_analytics"
                  labelIconSize={16}
                  helpImportant={
                    cisco_secure_malware_analytics.additional_information
                  }
                  timestamp={cisco_secure_malware_analytics.last_completed_on}
                  layout={LAYOUT_TYPE.COL}
                  checked={values.cisco_secure_malware_analytics}
                />
              </ReanalyzeCardContainer>

              <ReanalyzeCardContainer
                hidden={!cuckoo.enabled}
                selected={values.cuckoo}
                layout={LAYOUT_TYPE.COL}
                onClick={() => toggleValue("cuckoo")}
              >
                <ReanalyzeFormInput
                  name="cuckoo"
                  label="Cuckoo Sandbox"
                  labelIcon="cuckoo"
                  labelIconSize={16}
                  helpImportant={cuckoo.additional_information}
                  timestamp={cuckoo.last_completed_on}
                  layout={LAYOUT_TYPE.COL}
                  checked={values.cuckoo}
                />
              </ReanalyzeCardContainer>

              <ReanalyzeCardContainer
                hidden={!fireeye.enabled}
                selected={values.fireeye}
                layout={LAYOUT_TYPE.COL}
                onClick={() => toggleValue("fireeye")}
              >
                <ReanalyzeFormInput
                  name="fireeye"
                  label="FireEye Sandbox"
                  labelIcon="fireeye"
                  labelIconSize={16}
                  helpImportant={fireeye.additional_information}
                  timestamp={fireeye.last_completed_on}
                  layout={LAYOUT_TYPE.COL}
                  checked={values.fireeye}
                />
              </ReanalyzeCardContainer>

              <ReanalyzeCardContainer
                hidden={!joe.enabled}
                selected={values.joe}
                layout={LAYOUT_TYPE.COL}
                onClick={() => toggleValue("joe")}
              >
                <ReanalyzeFormInput
                  name="joe"
                  label="Joe Sandbox"
                  labelIcon="joe"
                  labelIconSize={16}
                  helpImportant={joe.additional_information}
                  timestamp={joe.last_completed_on}
                  layout={LAYOUT_TYPE.COL}
                  checked={values.joe}
                />
              </ReanalyzeCardContainer>

              <ReanalyzeCardContainer
                hidden={!vmray_tcbase.enabled}
                selected={values.vmray_tcbase}
                layout={LAYOUT_TYPE.COL}
                onClick={() => toggleValue("vmray_tcbase")}
              >
                <ReanalyzeFormInput
                  name="vmray_tcbase"
                  label="VMRay"
                  labelIcon="vmray_tcbase"
                  labelIconSize={16}
                  helpImportant={vmray_tcbase.additional_information}
                  timestamp={vmray_tcbase.last_completed_on}
                  layout={LAYOUT_TYPE.COL}
                  checked={values.vmray_tcbase}
                />
              </ReanalyzeCardContainer>
            </div>
          </RenderWhen>
        </div>
      </div>

      {showFilePasswordField && (
        <>
          <hr className={"my-25"} />

          <div>
            <Typography variant={"h5"} className={"mb-15"}>
              Protected File
            </Typography>

            <WidgetPassword
              id="file_password"
              vertical
              name={"file_password"}
              label={
                <FormLabel
                  label={"File Password"}
                  tooltipText={
                    "Archive and password will be discarded after unpacking. Zip archives with 1 child file are supported."
                  }
                />
              }
              placeholder={"Enter Password"}
              onChange={e => onFilePasswordChange(e.currentTarget.value)}
              className={
                areAdditionalAnalysisTypesAvailable(popupWindowData)
                  ? styles.password_field_large
                  : styles.password_field_medium
              }
            />
          </div>
        </>
      )}

      <Form.Group>
        <ButtonToolbar className="justify-content-end">
          <Button
            type="button"
            variant="prompt-secondary"
            onClick={onCancel}
            data-testid="reanalyze_form_cancel_button"
            disabled={isSubmitted}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="prompt-primary"
            disabled={isSubmitButtonDisabled || isSubmitted}
            id="reanalyze-popup-submit-btn"
            data-testid="reanalyze_form_reanalyze_button"
          >
            {isSubmitted ? (
              <Spinner animation="border" size="sm" className="me-0" />
            ) : (
              submitButtonLabel
            )}
          </Button>
        </ButtonToolbar>
      </Form.Group>
    </Form>
  );
};

export default form(ReanalyzeForm);
